import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type {
  Vehicle,
  CreateVehicleDto,
  UpdateVehicleDto,
  VehicleFilters,
} from '../../types/model/vehicle.model';
import { vehicleService } from '../../services/api';

// Estado inicial
export interface VehiclesState {
  vehicles: Vehicle[];
  selectedVehicle: Vehicle | null;
  isLoading: boolean;
  error: string | null;
  filters: VehicleFilters;
  totalCount: number;
}

const initialState: VehiclesState = {
  vehicles: [],
  selectedVehicle: null,
  isLoading: false,
  error: null,
  filters: {},
  totalCount: 0,
};

// ==================== ASYNC THUNKS ====================

// Obtener todos los vehículos
export const fetchVehicles = createAsyncThunk(
  'vehicles/fetchAll',
  async (filters: VehicleFilters | undefined, { rejectWithValue }) => {
    try {
      const vehicles = await vehicleService.getAll(filters);
      return vehicles;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar vehículos'
      );
    }
  }
);

// Obtener un vehículo por ID
export const fetchVehicleById = createAsyncThunk(
  'vehicles/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const vehicle = await vehicleService.getById(id);
      return vehicle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar vehículo'
      );
    }
  }
);

// Crear un nuevo vehículo
export const createVehicle = createAsyncThunk(
  'vehicles/create',
  async (vehicleData: CreateVehicleDto, { rejectWithValue }) => {
    try {
      const newVehicle = await vehicleService.create(vehicleData);
      return newVehicle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear vehículo'
      );
    }
  }
);

// Actualizar un vehículo
export const updateVehicle = createAsyncThunk(
  'vehicles/update',
  async ({ id, data }: { id: string; data: UpdateVehicleDto }, { rejectWithValue }) => {
    try {
      const updatedVehicle = await vehicleService.update(id, data);
      return updatedVehicle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar vehículo'
      );
    }
  }
);

// Eliminar un vehículo
export const deleteVehicle = createAsyncThunk(
  'vehicles/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await vehicleService.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar vehículo'
      );
    }
  }
);

// Marcar un vehículo como vendido
export const markVehicleAsSold = createAsyncThunk(
  'vehicles/markAsSold',
  async ({ id, fechaVenta }: { id: string; fechaVenta: string }, { rejectWithValue }) => {
    try {
      // Actualizar el vehículo con fecha de venta
      // Nota: El backend espera un UpdateVehicleDto, adaptamos según necesidad
      await vehicleService.update(id, {
        saleDate: fechaVenta,
        status: 'SOLD' as any
      });

      return { id, fechaVenta };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al marcar vehículo como vendido'
      );
    }
  }
);

// ==================== SLICE ====================

const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    // Seleccionar un vehículo
    selectVehicle: (state, action: PayloadAction<Vehicle | null>) => {
      state.selectedVehicle = action.payload;
    },

    // Limpiar vehículo seleccionado
    clearSelectedVehicle: (state) => {
      state.selectedVehicle = null;
    },

    // Actualizar filtros
    setFilters: (state, action: PayloadAction<VehicleFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Limpiar filtros
    clearFilters: (state) => {
      state.filters = {};
    },

    // Limpiar error
    clearVehiclesError: (state) => {
      state.error = null;
    },

    // Limpiar todo el estado
    resetVehiclesState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch all vehicles
    builder
      .addCase(fetchVehicles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles = action.payload;
        state.totalCount = action.payload.length;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch vehicle by ID
    builder
      .addCase(fetchVehicleById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicleById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedVehicle = action.payload;
      })
      .addCase(fetchVehicleById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create vehicle
    builder
      .addCase(createVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update vehicle
    builder
      .addCase(updateVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedVehicle = action.payload;
        const index = state.vehicles.findIndex((v) => v.id === updatedVehicle.id);
        if (index !== -1) {
          state.vehicles[index] = updatedVehicle;
        }
        if (state.selectedVehicle?.id === updatedVehicle.id) {
          state.selectedVehicle = updatedVehicle;
        }
      })
      .addCase(updateVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete vehicle
    builder
      .addCase(deleteVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles = state.vehicles.filter((v) => v.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedVehicle?.id === action.payload) {
          state.selectedVehicle = null;
        }
      })
      .addCase(deleteVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Mark as sold
    builder
      .addCase(markVehicleAsSold.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markVehicleAsSold.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.vehicles.findIndex((v) => v.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index].saleDate = action.payload.fechaVenta;
          state.vehicles[index].status = 'SOLD' as any;
        }
        if (state.selectedVehicle?.id === action.payload.id) {
          state.selectedVehicle.saleDate = action.payload.fechaVenta;
          state.selectedVehicle.status = 'SOLD' as any;
        }
      })
      .addCase(markVehicleAsSold.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Exportar acciones
export const {
  selectVehicle,
  clearSelectedVehicle,
  setFilters,
  clearFilters,
  clearVehiclesError,
  resetVehiclesState,
} = vehiclesSlice.actions;

// Selectores
export const selectAllVehicles = (state: RootState) => state.vehicles.vehicles;
export const selectSelectedVehicle = (state: RootState) => state.vehicles.selectedVehicle;
export const selectVehiclesLoading = (state: RootState) => state.vehicles.isLoading;
export const selectVehiclesError = (state: RootState) => state.vehicles.error;
export const selectVehicleFilters = (state: RootState) => state.vehicles.filters;
export const selectVehiclesTotalCount = (state: RootState) => state.vehicles.totalCount;

// Selector memoizado para vehículos filtrados
export const selectFilteredVehicles = createSelector(
  [selectAllVehicles, selectVehicleFilters],
  (vehicles, filters) => {
    return vehicles.filter((vehicle) => {
      // Filtro por marca
      if (filters.brand && !vehicle.brand.toLowerCase().includes(filters.brand.toLowerCase())) {
        return false;
      }

      // Filtro por tipo
      if (filters.vehicleType && vehicle.vehicleType !== filters.vehicleType) {
        return false;
      }

      // Filtro por condición
      if (filters.condition && vehicle.condition !== filters.condition) {
        return false;
      }

      // Filtro por año
      if (filters.minYear && vehicle.year < filters.minYear) {
        return false;
      }
      if (filters.maxYear && vehicle.year > filters.maxYear) {
        return false;
      }

      // Filtro por precio
      if (filters.minPrice && vehicle.price < filters.minPrice) {
        return false;
      }
      if (filters.maxPrice && vehicle.price > filters.maxPrice) {
        return false;
      }

      // Búsqueda general
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        return (
          vehicle.brand.toLowerCase().includes(searchTerm) ||
          vehicle.model.toLowerCase().includes(searchTerm) ||
          (vehicle.licensePlate && vehicle.licensePlate.toLowerCase().includes(searchTerm))
        );
      }

      return true;
    });
  }
);

// Selector para vehículos disponibles
export const selectAvailableVehicles = createSelector(
  [selectAllVehicles],
  (vehicles) => vehicles.filter((v) => v.status === 'AVAILABLE')
);

// Selector para vehículos vendidos
export const selectSoldVehicles = createSelector(
  [selectAllVehicles],
  (vehicles) => vehicles.filter((v) => v.status === 'SOLD')
);

// Exportar reducer
export default vehiclesSlice.reducer;
